import { Autocomplete, AutocompleteProps, createFilterOptions, TextField } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { useTheme } from '@dh/hooks';

import { DigitalHubChip } from '../buttons';

interface DHAutocompleteSingleProps<Value> {
    ariaLabel: string;
    options: Value[];
    name: string;
    value: Value | null;
    label: string;
    onChange: (key: string, value: Value | null) => void;
    getOptionId: (option: Value) => string;
    getOptionLabel: (option: Value) => string;
    isRequired?: boolean;
    disabled?: boolean;
    renderOption?: AutocompleteProps<Value, false, false, false>['renderOption'];
    renderInputValue?: (value: Value | null) => string;
}

export const DHAutocompleteSingle = <Value,>({
    ariaLabel,
    options,
    name,
    value,
    label,
    onChange,
    getOptionId,
    getOptionLabel,
    isRequired = false,
    disabled = false,
    renderOption,
    renderInputValue
}: DHAutocompleteSingleProps<Value>) => {
    const { colors } = useTheme();

    const t_forms = useTranslations('forms');
    const requiredPrefix = isRequired ? t_forms('mandatory_input') : '';
    const textFieldLabel = label + requiredPrefix;

    // Control the input value separately if renderInputValue is provided
    const [inputValue, setInputValue] = useState<string>('');

    // Sync inputValue with value changes
    useEffect(() => {
        if (renderInputValue && value) {
            setInputValue(renderInputValue(value));
        } else if (value) {
            setInputValue(getOptionLabel(value));
        } else {
            setInputValue('');
        }
    }, [value, renderInputValue, getOptionLabel]);

    // Custom input change handler that preserves the selected value
    const handleInputChange = (_: any, newInputValue: string, reason: string) => {
        // Only update inputValue when user is typing, not when blurring or resetting
        if (reason === 'input') {
            setInputValue(newInputValue);
        } else if (reason === 'reset' && value) {
            // On reset (blur/selection), restore the display value
            if (renderInputValue) {
                setInputValue(renderInputValue(value));
            } else {
                setInputValue(getOptionLabel(value));
            }
        }
    };

    return (
        <Autocomplete
            disabled={disabled}
            aria-label={ariaLabel}
            options={options}
            filterSelectedOptions
            filterOptions={createFilterOptions()}
            isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
            value={value}
            onChange={(_, value) => onChange(name, value)}
            {...(renderInputValue && {
                inputValue,
                onInputChange: handleInputChange
            })}
            renderValue={(tagValue, getItemProps) => (
                <DigitalHubChip
                    {...getItemProps()}
                    sx={{
                        backgroundColor: colors.primaryContainer,
                        margin: '4px 8px 4px 0px'
                    }}
                    label={getOptionLabel(tagValue)}
                />
            )}
            getOptionLabel={getOptionLabel}
            renderOption={
                renderOption ??
                ((props, option) => {
                    const index = (props as any)['data-option-index'];
                    return (
                        <li
                            {...props}
                            key={getOptionId(option)}
                            style={
                                index !== 0
                                    ? {
                                          borderTop: `1px solid ${colors.surfaceVariant}`,
                                          paddingTop: '12px'
                                      }
                                    : undefined
                            }
                        >
                            {getOptionLabel(option)}
                        </li>
                    );
                })
            }
            renderInput={({ InputProps, InputLabelProps, inputProps, ...params }) => (
                <TextField
                    // we set required to false to prevent the * marker. Instead, we add (Pflichtfeld) to the label and require the value in the form
                    required={false}
                    key={params.id}
                    label={textFieldLabel}
                    slotProps={{
                        input: {
                            ...InputProps,
                            sx: { height: 56 },
                            startAdornment: (
                                <Search
                                    sx={{
                                        color: disabled
                                            ? colors.disabledText
                                            : colors.onSurfaceVariant
                                    }}
                                />
                            )
                        },
                        inputLabel: InputLabelProps,
                        htmlInput: inputProps
                    }}
                    {...params}
                />
            )}
        />
    );
};
